import React, { useEffect, useState, useMemo } from "react";
import { apiClient } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import {
  Wrench, Plus, Trash2, Eye, EyeOff, ChevronRight, ChevronLeft,
  ArrowUp, ArrowDown, Save,
} from "lucide-react";

/* iter327 — Heavy Equipment taxonomy (Phase 2, Super Admin only).

   Two views:
     • Types list (default) — add / hide / delete / reorder types.
     • Per-type specs — open a type to manage its dynamic fields.

   Guard-rails:
     • Seeded (initial 60+) types cannot be deleted from the UI. The API also
       refuses. Use "hide" to remove them from the SellPage picker without
       breaking any existing ad that references them.
     • Field keys are immutable once created (to keep old ads' values
       readable). If you need a different key, delete the spec and re-add.
*/

const FIELD_TYPES = [
  { v: "text",    ar: "نص",       en: "Text" },
  { v: "number",  ar: "رقم",      en: "Number" },
  { v: "select",  ar: "قائمة",    en: "Select" },
  { v: "boolean", ar: "نعم / لا", en: "Boolean" },
];

const GROUPS = [
  ["trucks",           "شاحنات",         "Trucks"],
  ["tankers",          "صهاريج",         "Tankers"],
  ["buses",            "حافلات وفانات",  "Buses / Vans"],
  ["earthmoving",      "أعمال ترابية",   "Earthmoving"],
  ["lifting",          "رفع",            "Lifting / Cranes"],
  ["concrete_asphalt", "خرسانة وأسفلت",  "Concrete / Asphalt"],
  ["power",            "طاقة",           "Power"],
  ["storage",          "تخزين",          "Storage"],
  ["mining",           "تعدين وحفر",     "Mining / Drilling"],
  ["other",            "أخرى",           "Other"],
];

export default function HeavyTaxonomyTab() {
  const { lang } = useI18n();
  const [types, setTypes] = useState([]);
  const [selected, setSelected] = useState(null); // type_key when drilled-in
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/heavy-taxonomy/types");
      setTypes(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (selected) {
    const t = types.find((x) => x.key === selected);
    return (
      <SpecsView
        typeRow={t}
        lang={lang}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <TypesView
      lang={lang}
      loading={loading}
      types={types}
      onOpen={(k) => setSelected(k)}
      onChanged={load}
    />
  );
}

/* ─────────────────────────────────────────────────────────────
   TYPES LIST
   ───────────────────────────────────────────────────────────── */
function TypesView({ lang, loading, types, onOpen, onChanged }) {
  const [form, setForm] = useState({ key: "", ar: "", en: "", group: "other" });
  const [busy, setBusy] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of types) {
      const g = t.group || "other";
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(t);
    }
    return map;
  }, [types]);

  const isSeeded = (t) =>
    // Backend rejects delete for seeded; we mirror the hint in the UI.
    !!t && !!t.key && t.created_at && t.created_at.length > 0 && !t._custom;

  const add = async (e) => {
    e.preventDefault();
    if (!form.key.trim() || !form.ar.trim() || !form.en.trim()) return;
    setBusy(true);
    try {
      await apiClient.post("/admin/heavy-taxonomy/types", form);
      setForm({ key: "", ar: "", en: "", group: form.group });
      onChanged();
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, body) => {
    try {
      await apiClient.patch(`/admin/heavy-taxonomy/types/${id}`, body);
      onChanged();
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Error");
    }
  };

  const del = async (t) => {
    if (!window.confirm(lang === "ar"
      ? `حذف النوع "${t.ar || t.en}"؟ (الأنواع الأصلية لا يمكن حذفها — استخدم إخفاء)`
      : `Delete "${t.en}"? (Seeded types cannot be deleted — hide them instead)`)) return;
    try {
      await apiClient.delete(`/admin/heavy-taxonomy/types/${t.id}`);
      onChanged();
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Error");
    }
  };

  const move = async (t, dir) => {
    // Simple order swap with the neighbour in the same visible list.
    const sorted = [...types].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sorted.findIndex((x) => x.id === t.id);
    if (idx < 0) return;
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[j];
    await patch(a.id, { order: b.order });
    await patch(b.id, { order: a.order });
  };

  return (
    <div data-testid="admin-heavy-taxonomy">
      <div className="flex items-center gap-3 mb-4">
        <Wrench className="w-5 h-5 text-[#8a6f2e]" />
        <h2 className="font-display text-2xl">
          {lang === "ar" ? "معدات ثقيلة — الأنواع والمواصفات" : "Heavy Equipment — Types & Specs"}
        </h2>
        <span className="pill text-[10px]">{types.length}</span>
      </div>
      <p className="text-xs text-[#6b6b6b] mb-6 max-w-3xl">
        {lang === "ar"
          ? "أنشئ وعدّل أنواع المعدات والمواصفات الخاصة بكل نوع. الأنواع الأصلية الـ 63 لا يمكن حذفها لضمان عدم كسر الإعلانات القديمة — أخفها بدلاً من ذلك. ستظهر المواصفات في صفحة إضافة الإعلان تلقائيًا عند اختيار النوع المطابق."
          : "Add/edit heavy equipment types and their specs. The initial 63 seeded types cannot be deleted to protect existing ads — hide them instead. Specs will appear automatically on the SellPage when the matching type is selected."}
      </p>

      {/* Add new type form */}
      <form onSubmit={add} className="surface rounded-xl p-4 mb-6 grid sm:grid-cols-5 gap-3" data-testid="heavy-type-add-form">
        <input className="input-dark" placeholder={lang === "ar" ? "المفتاح (Key)" : "Key"}
               value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
               data-testid="heavy-type-input-key" />
        <input className="input-dark" placeholder={lang === "ar" ? "الاسم بالعربية" : "Arabic name"}
               value={form.ar} onChange={(e) => setForm({ ...form, ar: e.target.value })}
               data-testid="heavy-type-input-ar" />
        <input className="input-dark" placeholder={lang === "ar" ? "الاسم بالإنجليزية" : "English name"}
               value={form.en} onChange={(e) => setForm({ ...form, en: e.target.value })}
               data-testid="heavy-type-input-en" />
        <select className="input-dark" value={form.group}
                onChange={(e) => setForm({ ...form, group: e.target.value })}
                data-testid="heavy-type-input-group">
          {GROUPS.map(([v, ar, en]) => (
            <option key={v} value={v}>{lang === "ar" ? ar : en}</option>
          ))}
        </select>
        <button type="submit" disabled={busy}
                className="btn-primary text-sm inline-flex items-center gap-2 justify-center"
                data-testid="heavy-type-add-btn">
          <Plus className="w-4 h-4" />{lang === "ar" ? "إضافة نوع" : "Add type"}
        </button>
      </form>

      {loading ? (
        <div className="text-[#6b6b6b] text-sm p-4">{lang === "ar" ? "جارٍ التحميل…" : "Loading…"}</div>
      ) : (
        <div className="space-y-6">
          {GROUPS.map(([g, gAr, gEn]) => {
            const list = grouped.get(g) || [];
            if (list.length === 0) return null;
            return (
              <div key={g} data-testid={`heavy-group-${g}`}>
                <div className="text-xs uppercase tracking-widest text-[#8a6f2e] mb-2">
                  {lang === "ar" ? gAr : gEn} · {list.length}
                </div>
                <div className="surface rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-[#0a0a0a]/[0.04] text-[#3a3a3a] text-xs uppercase tracking-widest">
                      <tr>
                        <th className="p-2 text-start">{lang === "ar" ? "الترتيب" : "Order"}</th>
                        <th className="p-2 text-start">{lang === "ar" ? "المفتاح" : "Key"}</th>
                        <th className="p-2 text-start">{lang === "ar" ? "العربية" : "Arabic"}</th>
                        <th className="p-2 text-start">{lang === "ar" ? "الإنجليزية" : "English"}</th>
                        <th className="p-2 text-start">{lang === "ar" ? "الحالة" : "Visibility"}</th>
                        <th className="p-2 text-end">{lang === "ar" ? "إجراءات" : "Actions"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list
                        .sort((a, b) => (a.order || 0) - (b.order || 0))
                        .map((t) => (
                          <tr key={t.id} className="border-t border-[#0a0a0a]/[0.06]"
                              data-testid={`heavy-type-row-${t.key}`}>
                            <td className="p-2 whitespace-nowrap">
                              <div className="inline-flex items-center gap-1">
                                <button onClick={() => move(t, "up")} className="p-1 hover:bg-black/5 rounded"
                                        title={lang === "ar" ? "أعلى" : "Move up"}
                                        data-testid={`heavy-type-up-${t.key}`}>
                                  <ArrowUp className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => move(t, "down")} className="p-1 hover:bg-black/5 rounded"
                                        title={lang === "ar" ? "أسفل" : "Move down"}
                                        data-testid={`heavy-type-down-${t.key}`}>
                                  <ArrowDown className="w-3.5 h-3.5" />
                                </button>
                                <span className="text-[10px] text-[#8a8a8a] ml-1">{t.order}</span>
                              </div>
                            </td>
                            <td className="p-2 font-mono text-[11px]">{t.key}</td>
                            <td className="p-2">
                              <InlineText value={t.ar} onSave={(v) => patch(t.id, { ar: v })} />
                            </td>
                            <td className="p-2">
                              <InlineText value={t.en} onSave={(v) => patch(t.id, { en: v })} />
                            </td>
                            <td className="p-2">
                              <button
                                onClick={() => patch(t.id, { hidden: !t.hidden })}
                                className={`pill text-[10px] inline-flex items-center gap-1 ${
                                  t.hidden ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"
                                }`}
                                data-testid={`heavy-type-toggle-${t.key}`}
                              >
                                {t.hidden
                                  ? (<><EyeOff className="w-3 h-3" />{lang === "ar" ? "مخفي" : "Hidden"}</>)
                                  : (<><Eye className="w-3 h-3" />{lang === "ar" ? "ظاهر" : "Visible"}</>)}
                              </button>
                            </td>
                            <td className="p-2 text-end whitespace-nowrap">
                              <button
                                onClick={() => onOpen(t.key)}
                                className="text-xs rounded-full px-3 py-1.5 font-bold border border-[#c9a55c] hover:bg-[#c9a55c]/10 inline-flex items-center gap-1"
                                data-testid={`heavy-type-open-${t.key}`}
                              >
                                {lang === "ar" ? "المواصفات" : "Specs"}
                                {lang === "ar" ? <ChevronLeft className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                onClick={() => del(t)}
                                className="ms-2 text-xs rounded-full px-3 py-1.5 font-bold border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"
                                title={lang === "ar" ? "حذف (الأنواع الأصلية محمية)" : "Delete (seeded types are protected)"}
                                data-testid={`heavy-type-delete-${t.key}`}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   SPECS VIEW (per-type)
   ───────────────────────────────────────────────────────────── */
function SpecsView({ typeRow, lang, onBack }) {
  const [specs, setSpecs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    key: "",
    label_ar: "",
    label_en: "",
    field_type: "text",
    required: false,
    options: "",
    unit_ar: "",
    unit_en: "",
    placeholder_ar: "",
    placeholder_en: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!typeRow) return;
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/admin/heavy-taxonomy/types/${typeRow.key}/specs`);
      setSpecs(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeRow && typeRow.key]);

  if (!typeRow) {
    return (
      <div className="p-6 text-[#6b6b6b]">
        {lang === "ar" ? "النوع غير موجود." : "Type not found."}
        <button onClick={onBack} className="ms-2 underline">
          {lang === "ar" ? "رجوع" : "Back"}
        </button>
      </div>
    );
  }

  const add = async (e) => {
    e.preventDefault();
    if (!form.key.trim() || !form.label_ar.trim() || !form.label_en.trim()) return;
    const payload = {
      key: form.key.trim(),
      label_ar: form.label_ar.trim(),
      label_en: form.label_en.trim(),
      field_type: form.field_type,
      required: !!form.required,
      unit_ar: form.unit_ar || "",
      unit_en: form.unit_en || "",
      placeholder_ar: form.placeholder_ar || "",
      placeholder_en: form.placeholder_en || "",
      options: form.field_type === "select"
        ? form.options.split(",").map((s) => s.trim()).filter(Boolean)
        : undefined,
    };
    setBusy(true);
    try {
      await apiClient.post(`/admin/heavy-taxonomy/types/${typeRow.key}/specs`, payload);
      setForm({
        key: "", label_ar: "", label_en: "", field_type: "text",
        required: false, options: "", unit_ar: "", unit_en: "",
        placeholder_ar: "", placeholder_en: "",
      });
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Error");
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, body) => {
    try {
      await apiClient.patch(`/admin/heavy-taxonomy/specs/${id}`, body);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Error");
    }
  };

  const del = async (s) => {
    if (!window.confirm(lang === "ar"
      ? `حذف الحقل "${s.label_ar || s.label_en}"؟`
      : `Delete field "${s.label_en}"?`)) return;
    try {
      await apiClient.delete(`/admin/heavy-taxonomy/specs/${s.id}`);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Error");
    }
  };

  const move = async (s, dir) => {
    const sorted = [...specs].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sorted.findIndex((x) => x.id === s.id);
    if (idx < 0) return;
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= sorted.length) return;
    const a = sorted[idx];
    const b = sorted[j];
    await patch(a.id, { order: b.order });
    await patch(b.id, { order: a.order });
  };

  return (
    <div data-testid="admin-heavy-taxonomy-specs">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button onClick={onBack}
                className="text-xs rounded-full px-3 py-1.5 font-bold border border-[#0a0a0a]/20 hover:bg-black/5 inline-flex items-center gap-1"
                data-testid="heavy-specs-back">
          {lang === "ar" ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronLeft className="w-3.5 h-3.5" />}
          {lang === "ar" ? "رجوع للأنواع" : "Back to types"}
        </button>
        <Wrench className="w-5 h-5 text-[#8a6f2e]" />
        <h2 className="font-display text-2xl">
          {lang === "ar" ? `مواصفات: ${typeRow.ar}` : `Specs: ${typeRow.en}`}
        </h2>
        <span className="pill text-[10px] font-mono">{typeRow.key}</span>
      </div>

      <p className="text-xs text-[#6b6b6b] mb-4 max-w-3xl">
        {lang === "ar"
          ? "أضف الحقول التي ستظهر للبائع عند اختيار هذا النوع في صفحة إضافة الإعلان. مفتاح الحقل ثابت بعد الإنشاء (لحماية بيانات الإعلانات القائمة)."
          : "Add fields that appear on the SellPage when the seller picks this type. Field keys are immutable after creation (to keep old ads' values readable)."}
      </p>

      {/* Add spec form */}
      <form onSubmit={add} className="surface rounded-xl p-4 mb-6 grid md:grid-cols-6 gap-3" data-testid="heavy-spec-add-form">
        <input className="input-dark" placeholder={lang === "ar" ? "المفتاح (مثال: boom_length_m)" : "Key (e.g. boom_length_m)"}
               value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
               data-testid="heavy-spec-input-key" />
        <input className="input-dark" placeholder={lang === "ar" ? "التسمية بالعربية" : "Arabic label"}
               value={form.label_ar} onChange={(e) => setForm({ ...form, label_ar: e.target.value })}
               data-testid="heavy-spec-input-labelar" />
        <input className="input-dark" placeholder={lang === "ar" ? "التسمية بالإنجليزية" : "English label"}
               value={form.label_en} onChange={(e) => setForm({ ...form, label_en: e.target.value })}
               data-testid="heavy-spec-input-labelen" />
        <select className="input-dark" value={form.field_type}
                onChange={(e) => setForm({ ...form, field_type: e.target.value })}
                data-testid="heavy-spec-input-type">
          {FIELD_TYPES.map((f) => (
            <option key={f.v} value={f.v}>{lang === "ar" ? f.ar : f.en}</option>
          ))}
        </select>
        <div className="flex items-center gap-2">
          <input id="hspec-req" type="checkbox" checked={form.required}
                 onChange={(e) => setForm({ ...form, required: e.target.checked })}
                 data-testid="heavy-spec-input-required" />
          <label htmlFor="hspec-req" className="text-xs">
            {lang === "ar" ? "إلزامي" : "Required"}
          </label>
        </div>
        <button type="submit" disabled={busy}
                className="btn-primary text-sm inline-flex items-center gap-2 justify-center"
                data-testid="heavy-spec-add-btn">
          <Plus className="w-4 h-4" />{lang === "ar" ? "إضافة حقل" : "Add field"}
        </button>

        <input className="input-dark" placeholder={lang === "ar" ? "الوحدة بالعربية (م، طن…)" : "Unit AR (m, t…)"}
               value={form.unit_ar} onChange={(e) => setForm({ ...form, unit_ar: e.target.value })}
               data-testid="heavy-spec-input-unitar" />
        <input className="input-dark" placeholder={lang === "ar" ? "الوحدة بالإنجليزية" : "Unit EN"}
               value={form.unit_en} onChange={(e) => setForm({ ...form, unit_en: e.target.value })}
               data-testid="heavy-spec-input-uniten" />
        <input className="input-dark" placeholder={lang === "ar" ? "Placeholder AR" : "Placeholder AR"}
               value={form.placeholder_ar} onChange={(e) => setForm({ ...form, placeholder_ar: e.target.value })}
               data-testid="heavy-spec-input-phar" />
        <input className="input-dark" placeholder={lang === "ar" ? "Placeholder EN" : "Placeholder EN"}
               value={form.placeholder_en} onChange={(e) => setForm({ ...form, placeholder_en: e.target.value })}
               data-testid="heavy-spec-input-phen" />
        {form.field_type === "select" ? (
          <input className="input-dark md:col-span-2"
                 placeholder={lang === "ar" ? "خيارات مفصولة بفواصل: خيار1, خيار2" : "Comma-separated options: opt1, opt2"}
                 value={form.options} onChange={(e) => setForm({ ...form, options: e.target.value })}
                 data-testid="heavy-spec-input-options" />
        ) : (
          <div className="md:col-span-2" />
        )}
      </form>

      {loading ? (
        <div className="text-[#6b6b6b] text-sm p-4">{lang === "ar" ? "جارٍ التحميل…" : "Loading…"}</div>
      ) : specs.length === 0 ? (
        <div className="surface rounded-xl p-8 text-center text-sm text-[#6b6b6b]" data-testid="heavy-specs-empty">
          {lang === "ar"
            ? "لا توجد حقول مخصصة بعد. أضف حقلًا من الأعلى ليظهر تلقائيًا في صفحة إضافة الإعلان."
            : "No custom fields yet. Add one above — it will appear on the SellPage automatically."}
        </div>
      ) : (
        <div className="surface rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#0a0a0a]/[0.04] text-[#3a3a3a] text-xs uppercase tracking-widest">
              <tr>
                <th className="p-2 text-start">{lang === "ar" ? "الترتيب" : "Order"}</th>
                <th className="p-2 text-start">{lang === "ar" ? "المفتاح" : "Key"}</th>
                <th className="p-2 text-start">{lang === "ar" ? "التسمية" : "Label"}</th>
                <th className="p-2 text-start">{lang === "ar" ? "النوع" : "Type"}</th>
                <th className="p-2 text-start">{lang === "ar" ? "الوحدة" : "Unit"}</th>
                <th className="p-2 text-start">{lang === "ar" ? "إلزامي" : "Required"}</th>
                <th className="p-2 text-end">{lang === "ar" ? "إجراءات" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {[...specs].sort((a, b) => (a.order || 0) - (b.order || 0)).map((s) => (
                <tr key={s.id} className="border-t border-[#0a0a0a]/[0.06]" data-testid={`heavy-spec-row-${s.key}`}>
                  <td className="p-2 whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => move(s, "up")} className="p-1 hover:bg-black/5 rounded">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => move(s, "down")} className="p-1 hover:bg-black/5 rounded">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-[10px] text-[#8a8a8a] ml-1">{s.order}</span>
                    </div>
                  </td>
                  <td className="p-2 font-mono text-[11px]">{s.key}</td>
                  <td className="p-2">
                    <div className="space-y-1">
                      <InlineText value={s.label_ar} onSave={(v) => patch(s.id, { label_ar: v })} />
                      <InlineText value={s.label_en} onSave={(v) => patch(s.id, { label_en: v })} />
                    </div>
                    {s.field_type === "select" && (
                      <InlineText
                        value={(s.options || []).join(", ")}
                        onSave={(v) => patch(s.id, { options: v.split(",").map((x) => x.trim()).filter(Boolean) })}
                        placeholder="opt1, opt2"
                      />
                    )}
                  </td>
                  <td className="p-2">
                    <select
                      className="input-dark text-xs"
                      value={s.field_type}
                      onChange={(e) => patch(s.id, { field_type: e.target.value })}
                    >
                      {FIELD_TYPES.map((f) => (
                        <option key={f.v} value={f.v}>{lang === "ar" ? f.ar : f.en}</option>
                      ))}
                    </select>
                  </td>
                  <td className="p-2 text-xs">
                    <div className="space-y-1">
                      <InlineText value={s.unit_ar || ""} onSave={(v) => patch(s.id, { unit_ar: v })} placeholder="AR" />
                      <InlineText value={s.unit_en || ""} onSave={(v) => patch(s.id, { unit_en: v })} placeholder="EN" />
                    </div>
                  </td>
                  <td className="p-2">
                    <input type="checkbox" checked={!!s.required}
                           onChange={(e) => patch(s.id, { required: e.target.checked })} />
                  </td>
                  <td className="p-2 text-end">
                    <button
                      onClick={() => del(s)}
                      className="text-xs rounded-full px-3 py-1.5 font-bold border border-red-300 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"
                      data-testid={`heavy-spec-delete-${s.key}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   Small inline editable text cell.
   ───────────────────────────────────────────────────────────── */
function InlineText({ value, onSave, placeholder = "" }) {
  const [v, setV] = useState(value || "");
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setV(value || ""); setDirty(false); }, [value]);
  return (
    <div className="inline-flex items-center gap-1 w-full">
      <input
        className="input-dark text-xs flex-1"
        value={v}
        placeholder={placeholder}
        onChange={(e) => { setV(e.target.value); setDirty(e.target.value !== (value || "")); }}
      />
      {dirty && (
        <button
          onClick={() => onSave(v)}
          className="p-1 rounded hover:bg-black/5 text-[#8a6f2e]"
          title="Save"
        >
          <Save className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
